import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Copy } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { GermanDateInput } from "@/components/GermanDateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GOAL_CATS } from "@/lib/auditGoals";
import { AuditGoalEditor } from "@/components/AuditGoalEditor";

const blank = () => ({ label: "", start: "", end: "", custom_goals: [], qualitative: [] });
const num = (v) => (v === "" || v == null ? null : Number(String(v).replace(",", ".")));

function QualitativeEditor({ items, onChange }) {
  const upd = (i, patch) => onChange(items.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  return (
    <div className="space-y-2" data-testid="audit-qual-editor">
      {items.map((q, i) => (
        <div key={q.id || i} className="border border-slate-200 p-2 space-y-2" data-testid={`audit-qual-${i}`}>
          <div className="flex gap-2">
            <input value={q.text} onChange={(e) => upd(i, { text: e.target.value })} placeholder="z. B. Digitalisierung der Verwaltung" className="input-tech flex-1" data-testid={`audit-qual-text-${i}`} />
            <select value={q.status} onChange={(e) => upd(i, { status: e.target.value })} className="input-tech !w-32 shrink-0" data-testid={`audit-qual-status-${i}`}>
              <option value="offen">offen</option><option value="teilweise">teilweise</option><option value="erreicht">erreicht</option>
            </select>
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600" data-testid={`audit-qual-del-${i}`}><Trash2 size={14} /></button>
          </div>
          <input value={q.note} onChange={(e) => upd(i, { note: e.target.value })} placeholder="Bemerkung / Nachweis" className="input-tech" data-testid={`audit-qual-note-${i}`} />
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, { text: "", status: "offen", note: "" }])} className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1" data-testid="audit-qual-add"><Plus size={12} /> Qualitatives Ziel hinzufügen</button>
    </div>
  );
}

export default function AuditPeriodDialog({ open, onClose, period, onSaved }) {
  const [f, setF] = useState(blank());
  useEffect(() => {
    if (open) setF(period ? { label: period.label, start: period.start, end: period.end, custom_goals: period.custom_goals || [], qualitative: period.qualitative || [] } : blank());
  }, [open, period]);
  const copyPrev = async () => {
    try {
      const { data } = await api.get("/audit/periods");
      const ref = f.start || "9999-12-31";
      const prev = data.filter((p) => p.period_id !== period?.period_id && p.end < ref).sort((a, b) => b.end.localeCompare(a.end))[0];
      if (!prev) return toast.error("Kein vorheriger Audit-Zeitraum gefunden");
      const have = new Set(f.custom_goals.map((g) => g.label.trim().toLowerCase()));
      const goals = (prev.custom_goals || []).filter((g) => !have.has(g.label.trim().toLowerCase()))
        .map((g) => ({ category: g.category, label: g.label, target: g.target ?? "", actual: "", unit: g.unit, direction: g.direction, note: "" }));
      const haveQ = new Set(f.qualitative.map((q) => q.text.trim().toLowerCase()));
      const qual = (prev.qualitative || []).filter((q) => !haveQ.has(q.text.trim().toLowerCase())).map((q) => ({ text: q.text, status: "offen", note: "" }));
      if (!goals.length && !qual.length) return toast.info("Alle Ziele aus dem letzten Zeitraum sind bereits vorhanden");
      setF({ ...f, custom_goals: [...f.custom_goals, ...goals], qualitative: [...f.qualitative, ...qual] });
      toast.success(`${goals.length + qual.length} Ziel(e) aus „${prev.label}" übernommen`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const save = async () => {
    if (!f.label.trim() || !f.start || !f.end) return toast.error("Bitte Bezeichnung, Start und Ende angeben");
    if (f.end < f.start) return toast.error("Das Enddatum darf nicht vor dem Startdatum liegen");
    const custom_goals = f.custom_goals.map((g) => ({ ...g, target: num(g.target), actual: num(g.actual) }));
    const body = { label: f.label, start: f.start, end: f.end, custom_goals, qualitative: f.qualitative };
    try {
      const { data } = period ? await api.put(`/audit/periods/${period.period_id}`, body) : await api.post("/audit/periods", body);
      toast.success("Audit-Zeitraum gespeichert");
      onSaved(data.period_id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-white max-h-[90vh] overflow-y-auto" data-testid="audit-period-dialog">
        <DialogHeader><DialogTitle>{period ? "Audit-Zeitraum bearbeiten" : "Neuer Audit-Zeitraum"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="z. B. Audit 2026" className="input-tech" data-testid="audit-period-label" />
          <div className="grid grid-cols-2 gap-3">
            <GermanDateInput value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} placeholder="Start" data-testid="audit-period-start" />
            <GermanDateInput value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} placeholder="Ende" data-testid="audit-period-end" />
          </div>
          <button type="button" onClick={copyPrev} className="text-xs border border-slate-300 px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1" data-testid="audit-copy-prev-goals"><Copy size={12} /> Ziele aus letztem Zeitraum übernehmen</button>
          {GOAL_CATS.map(([cat, title]) => (
            <div key={cat} className="space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 pt-3">{title}</div>
              <AuditGoalEditor category={cat} goals={f.custom_goals} onChange={(g) => setF({ ...f, custom_goals: g })} />
              {cat === "unternehmen" && (
                <>
                  <div className="text-xs text-slate-500 pt-1">Qualitative Ziele</div>
                  <QualitativeEditor items={f.qualitative} onChange={(q) => setF({ ...f, qualitative: q })} />
                </>
              )}
            </div>
          ))}
          <button onClick={save} className="w-full bg-slate-900 text-white py-3 text-sm font-medium hover:bg-black" data-testid="audit-period-save">Speichern</button>
          {period && (
            <button onClick={async () => {
              if (!window.confirm(`Audit-Zeitraum „${period.label}" wirklich löschen?`)) return;
              await api.delete(`/audit/periods/${period.period_id}`);
              toast.success("Zeitraum gelöscht");
              onSaved(null);
            }} className="w-full text-xs text-red-600 hover:underline py-1" data-testid="audit-period-delete">Zeitraum löschen</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
